import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft } from 'lucide-react';
import { CASH_MOVEMENT_KINDS, formatMoney, PAYMENT_METHODS, type CurrencyCode, type PaymentMethod } from '@tengesa/shared';
import { api } from '../../lib/api';
import { useSession } from '../../app/session';
import { useCurrentBranch } from '../../app/Shell';
import { Badge, Button, Dialog, EmptyState, ErrorNotice, Field, FormError, PageHeader, Panel, Select, Spinner, TextInput, formatDateTime, useToast } from '../../ui';
import { CashMovementDialog, CloseShiftDialog, OpenShiftCard, useCurrentShift, type ShiftDetail } from './shared';

interface ShiftRow {
  id: string;
  number: string;
  status: 'open' | 'closed' | 'reconciled';
  openedAt: string;
  closedAt: string | null;
  cashierName: string;
  tillName: string;
  branchName: string;
  salesTotalCents: number;
  varianceCents: number | null;
  otherCurrencyVariance: boolean;
}

const STATUS = {
  open: { label: 'Open', tone: 'gold' as const },
  closed: { label: 'Awaiting sign-off', tone: 'warn' as const },
  reconciled: { label: 'Signed off', tone: 'ok' as const },
};

export function ShiftsPage() {
  const { me, can } = useSession();
  const { branch } = useCurrentBranch();
  const cur = me.business.baseCurrency as CurrencyCode;
  const [status, setStatus] = useState('');
  const mine = useCurrentShift(branch?.id);
  const q = useQuery({ queryKey: ['shifts', status], queryFn: () => api.get<ShiftRow[]>(`/api/shifts${status ? `?status=${status}` : ''}`) });

  return (
    <>
      <PageHeader title="Till shifts" description={can('shifts.reconcile') ? 'Every till session, what it took and whether the cash balanced.' : 'Your till sessions.'} />
      {can('shifts.operate') && branch && mine.data === null && (
        <div className="mb-6">
          <OpenShiftCard branchId={branch.id} />
        </div>
      )}
      <Panel>
        <div className="flex items-center gap-3 border-b border-line px-5 py-3">
          <Select aria-label="Status" className="max-w-56" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All shifts</option>
            <option value="open">Open</option>
            <option value="closed">Awaiting sign-off</option>
            <option value="reconciled">Signed off</option>
          </Select>
        </div>
        {q.isLoading && <Spinner />}
        {q.error && <div className="p-4"><ErrorNotice error={q.error} /></div>}
        {q.data?.length === 0 && <EmptyState title="No shifts" />}
        {!!q.data?.length && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2.5 font-medium">Shift</th>
                  <th className="px-3 py-2.5 font-medium">Cashier</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="px-3 py-2.5 text-right font-medium">Sales</th>
                  <th className="px-5 py-2.5 text-right font-medium">Cash difference</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {q.data.map((s) => (
                  <tr key={s.id} className="hover:bg-ledger-50/60">
                    <td className="px-5 py-3">
                      <Link to={`/shifts/${s.id}`} className="font-medium text-ledger-700 hover:underline">{s.number}</Link>
                      <span className="block text-xs text-muted">{s.tillName}, {s.branchName} — {formatDateTime(s.openedAt)}</span>
                    </td>
                    <td className="px-3 py-3">{s.cashierName}</td>
                    <td className="px-3 py-3"><Badge tone={STATUS[s.status].tone}>{STATUS[s.status].label}</Badge></td>
                    <td className="px-3 py-3 text-right num">{formatMoney(s.salesTotalCents, cur)}</td>
                    <td className={clsx('px-5 py-3 text-right num', (s.varianceCents ?? 0) < 0 && 'text-danger', (s.varianceCents ?? 0) > 0 && 'text-warn')}>
                      {s.status === 'open' ? '—' : s.varianceCents ? `${s.varianceCents > 0 ? '+' : ''}${formatMoney(s.varianceCents, cur)}` : 'Balanced'}
                      {s.otherCurrencyVariance && <span className="block text-xs text-warn">Other currency differs</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}

export function ShiftDetailPage() {
  const { id } = useParams();
  const { me, can } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['shift', id], queryFn: () => api.get<ShiftDetail>(`/api/shifts/${id}`) });
  const [closing, setClosing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [signing, setSigning] = useState(false);
  const [note, setNote] = useState('');
  const sign = useMutation({
    mutationFn: () => api.post<ShiftDetail>(`/api/shifts/${id}/reconcile`, { note: note || null }),
    onSuccess: (s) => {
      qc.setQueryData(['shift', id], s);
      void qc.invalidateQueries({ queryKey: ['shifts'] });
      setSigning(false);
      toast(`${s.number} signed off`);
    },
  });
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorNotice error={q.error} />;
  const s = q.data!;
  const mine = s.cashierId === me.user.id;
  const hasVariance = s.cash.some((c) => c.varianceCents);
  const rows: [string, keyof ShiftDetail['cash'][number], 1 | -1][] = [
    ['Float at opening', 'openingFloatCents', 1],
    ['Cash sales', 'cashSalesCents', 1],
    ['Change given', 'changeGivenCents', -1],
    ['Cash refunds', 'cashRefundsCents', -1],
    ['Account payments', 'accountPaymentsCents', 1],
    ['Order deposits', 'depositsCents', 1],
    ['Deposits refunded', 'depositRefundsCents', -1],
    ['Cash added', 'payInsCents', 1],
    ['Paid out', 'payOutsCents', -1],
    ['Moved to safe', 'dropsCents', -1],
    ['Expenses paid', 'expensesCents', -1],
    ['Suppliers paid', 'supplierPaymentsCents', -1],
  ];
  return (
    <>
      <Link to="/shifts" className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-ledger-700 hover:underline">
        <ArrowLeft className="size-4" /> All shifts
      </Link>
      <PageHeader
        title={`${s.number}`}
        description={`${s.cashierName} on ${s.tillName}, ${s.branchName}. Opened ${formatDateTime(s.openedAt)}${s.closedAt ? `, closed ${formatDateTime(s.closedAt)}` : ''}.`}
        actions={
          <>
            {s.status === 'open' && (mine || can('shifts.reconcile')) && <Button onClick={() => setMoving(true)}>Cash in or out</Button>}
            {s.status === 'open' && (mine || can('shifts.reconcile')) && <Button variant="primary" onClick={() => setClosing(true)}>Close shift</Button>}
            {s.status === 'closed' && can('shifts.reconcile') && (!mine || me.user.roleName === 'Owner') && <Button variant="primary" onClick={() => setSigning(true)}>Sign off</Button>}
          </>
        }
      />
      <div className="mb-6 flex flex-wrap gap-2">
        <Badge tone={STATUS[s.status].tone}>{STATUS[s.status].label}</Badge>
        <Badge>{s.saleCount} sales</Badge>
        {s.voidedCount > 0 && <Badge tone="warn">{s.voidedCount} voided</Badge>}
        {s.reconciledByName && <Badge tone="ok">Signed off by {s.reconciledByName}</Badge>}
      </div>
      {s.blind && (
        <p className="mb-6 rounded-[8px] bg-paper px-4 py-3 text-muted">
          You'll see what the drawer should hold after you close the shift and enter your count.
        </p>
      )}
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Panel title="Cash in the drawer">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2 font-medium" />
                  {s.cash.map((c) => <th key={c.currency} className="px-5 py-2 text-right font-medium">{c.currency}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows
                  .filter(([, k]) => s.cash.some((c) => (c[k] as number) !== 0))
                  .map(([label, k, sign]) => (
                    <tr key={k}>
                      <td className="px-5 py-2">{label}</td>
                      {s.cash.map((c) => (
                        <td key={c.currency} className="px-5 py-2 text-right num">
                          {(c[k] as number) ? `${sign < 0 ? '−' : ''}${formatMoney(c[k] as number, c.currency as CurrencyCode)}` : ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                {!s.blind && (
                  <tr className="font-semibold">
                    <td className="px-5 py-2">Should be in the drawer</td>
                    {s.cash.map((c) => <td key={c.currency} className="px-5 py-2 text-right num">{formatMoney(c.expectedCents, c.currency as CurrencyCode)}</td>)}
                  </tr>
                )}
                {s.status !== 'open' && (
                  <>
                    <tr>
                      <td className="px-5 py-2">Counted</td>
                      {s.cash.map((c) => <td key={c.currency} className="px-5 py-2 text-right num">{formatMoney(c.countedCents ?? 0, c.currency as CurrencyCode)}</td>)}
                    </tr>
                    <tr className="font-semibold">
                      <td className="px-5 py-2">Difference</td>
                      {s.cash.map((c) => (
                        <td key={c.currency} className={clsx('px-5 py-2 text-right num', (c.varianceCents ?? 0) < 0 && 'text-danger', (c.varianceCents ?? 0) > 0 && 'text-warn')}>
                          {c.varianceCents ? `${c.varianceCents > 0 ? 'Over ' : 'Short '}${formatMoney(Math.abs(c.varianceCents), c.currency as CurrencyCode)}` : 'Balanced'}
                        </td>
                      ))}
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
          {(s.closeNote || s.reconcileNote) && (
            <div className="border-t border-line px-5 py-3 text-sm text-muted">
              {s.closeNote && <p>Cashier: {s.closeNote}</p>}
              {s.reconcileNote && <p>Sign-off: {s.reconcileNote}</p>}
            </div>
          )}
        </Panel>
        <div className="space-y-6">
          <Panel title="Other payments taken">
            <p className="px-5 pt-3 text-sm text-muted">Check these against EcoCash statements, card machine batches and bank alerts.</p>
            <ul className="divide-y divide-line">
              {s.nonCash.length === 0 && <li className="px-5 py-3 text-muted">None.</li>}
              {s.nonCash.map((n) => (
                <li key={`${n.method}-${n.currency}`} className="flex justify-between px-5 py-2.5">
                  <span>{PAYMENT_METHODS[n.method as PaymentMethod]?.label.split(' (')[0] ?? n.method}</span>
                  <span className="num">{formatMoney(n.totalCents, n.currency as CurrencyCode)}</span>
                </li>
              ))}
            </ul>
          </Panel>
          {s.movements.length > 0 && (
            <Panel title="Cash moved">
              <ul className="divide-y divide-line">
                {s.movements.map((m) => (
                  <li key={m.id} className="flex justify-between gap-3 px-5 py-2.5 text-sm">
                    <span>
                      {CASH_MOVEMENT_KINDS[m.kind]}: {m.reason}
                      <span className="block text-xs text-muted">{formatDateTime(m.createdAt)} by {m.userName}</span>
                    </span>
                    <span className="num">{formatMoney(m.amountCents, m.currency as CurrencyCode)}</span>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </div>
      </div>
      {closing && <CloseShiftDialog shift={s} onClose={() => setClosing(false)} />}
      {moving && <CashMovementDialog shift={s} onClose={() => setMoving(false)} />}
      {signing && (
        <Dialog
          open
          onClose={() => setSigning(false)}
          title={`Sign off ${s.number}?`}
          description={hasVariance ? 'The cash did not balance. Record what you found out.' : 'The cash balanced.'}
          footer={
            <>
              <Button onClick={() => setSigning(false)}>Cancel</Button>
              <Button variant="primary" disabled={hasVariance && note.trim().length < 3} busy={sign.isPending} onClick={() => sign.mutate()}>Sign off</Button>
            </>
          }
        >
          <FormError error={sign.error} />
          <Field label={hasVariance ? 'Explanation' : 'Note (optional)'} htmlFor="sign-note">
            <TextInput id="sign-note" value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
          </Field>
        </Dialog>
      )}
    </>
  );
}
